import { SearchParam } from "../model/SearchParam.js";
import {SearchParamKeyDefinition} from "../model/SearchParamKeyDefinition";

// デフォルトのジョブキーワード（SQSL形式のフォールバック用）
const DEFAULT_JOB_KEYWORD = 'jobs';

/**
 * 検索文字列からパラメータ取得
 *
 * @param {string} searchString
 * @return {SearchParam}
 */
export const decomposeSearchStringToParams = (searchString) => {
    // SQSL形式かどうかを判定（-s-q- または -s-l- を含む）
    const isSqslFormat = searchString.includes('-s-q-') || searchString.includes('-s-l-');
    
    if (isSqslFormat) {
        return decomposeSqslFormatUrlToParams(searchString);
    } else {
        return decomposeJobsInFormatUrlToParams(searchString);
    }
};

/**
 * SQSL形式URLのパース処理
 * パターン: {job_keyword}-s-q-{keyword}-s-l-{location}
 * 
 * @param {string} searchString 
 * @returns {SearchParam}
 */
const decomposeSqslFormatUrlToParams = (searchString) => {
    let searchKeyword = '';
    let searchLocation = '';
    
    // クエリパラメータ部分を分離
    const [urlPart, queryPart] = searchString.split('?');
    
    // キーワード取得: -s-q-{keyword} の部分
    if (urlPart.includes('-s-q-')) {
        const keywordPart = urlPart.split('-s-q-')[1];
        
        if (urlPart.includes('-s-l-')) {
            // キーワード+勤務地の場合: -s-q-{keyword}-s-l-{location}
            searchKeyword = keywordPart.split('-s-l-')[0];
        } else {
            // キーワードのみの場合: -s-q-{keyword}
            searchKeyword = keywordPart;
        }
        
        searchKeyword = searchKeyword.replaceAll('-', ' ');
    }
    
    // 勤務地取得: -s-l-{location} の部分
    if (urlPart.includes('-s-l-')) {
        searchLocation = urlPart.split('-s-l-')[1];
        searchLocation = searchLocation.replaceAll('-', ' ');
    }
    
    // 絞り込みパラメータは既存と同じ処理
    const filters = getFiltersFromQuery(queryPart);
    
    return new SearchParam(searchKeyword, searchLocation, filters);
};

/**
 * Jobs-In形式URLからパラメータを取得
 * パターン: {keyword}-jobs-in-{location}
 * 
 * @param {string} searchString 
 * @returns {SearchParam}
 */
const decomposeJobsInFormatUrlToParams = (searchString) => {
    let searchKeyword = '';
    let searchLocation = '';

    // クエリパラメータ部分を分離
    const [urlPart, queryPart] = searchString.split('?');

    // キーワード取得
    if (urlPart.includes('-jobs')) {
        searchKeyword = urlPart.split('-jobs')[0];
        searchKeyword = searchKeyword.replaceAll('-', ' ');
    }

    // 勤務地取得
    if (urlPart.includes('jobs-in-')) {
        let searchLocationTemp = urlPart.split('jobs-in-')[1];
        searchLocation = searchLocationTemp.replaceAll('-', ' ');
    }

    const filters = getFiltersFromQuery(queryPart);

    return new SearchParam(searchKeyword, searchLocation, filters);
}

/**
 * クエリパラメータから絞り込みパラメータを取得
 * 
 * @param {string} queryPart 
 * @returns {Object}
 */
const getFiltersFromQuery = (queryPart) => {
    // 絞り込みパラメータ取得
    const filters = {
        [SearchParamKeyDefinition.distance]: '',
        [SearchParamKeyDefinition.datePosted]: '',
        [SearchParamKeyDefinition.salaryType]: '',
        [SearchParamKeyDefinition.salaryMin]: '',
        [SearchParamKeyDefinition.salaryMax]: '',
        [SearchParamKeyDefinition.jobType]: '',
        [SearchParamKeyDefinition.features]: [],
        [SearchParamKeyDefinition.type]: '',
        [SearchParamKeyDefinition.page]: '',
        [SearchParamKeyDefinition.uniqueId]: '',
    };

    if (queryPart) {
        const filterParams = queryPart.split('&');
        for (const param of filterParams) {
            const key = param.split('=')[0];
            let value = param.split('=')[1];

            if (key === 'f') {
                value = value.split('%2C');
            }

            filters[key] = value;
        }
    }

    return filters;
}

export const decomposeSearchStringToParamsForDisplay = (searchString) => {
    const searchParam = decomposeSearchStringToParams(searchString);
    //search_locations.name_in_urlとname_in_boxの差分の暫定対応
    //末尾が-Singaporeの時は、削除
    if (searchParam.location.endsWith(' Singapore')) {
        searchParam.location = searchParam.location.replace(/ Singapore$/, '');
    }

    return searchParam;
}

/**
 * パラメータから検索文字列(検索URL末尾の文字列)を生成
 *
 * @param {SearchParam} searchParam
 * @return {string}
 */
export const composeSearchStringFromParams = (searchParam) => {
    // 現在のURLパスからSQSL形式かどうかを判定
    const currentPath = window.location.pathname;
    const isSqslFormat = currentPath.includes('-s-q-') || currentPath.includes('-s-l-');
    
    if (isSqslFormat) {
        return composeSqslFormatSearchString(searchParam);
    } else {
        return composeJobsInFormatSearchString(searchParam);
    }
};

/**
 * SQSL形式の検索文字列を生成
 * パターン: {job_keyword}-s-q-{keyword}-s-l-{location}
 *
 * @param {SearchParam} searchParam
 * @return {string}
 */
const composeSqslFormatSearchString = (searchParam) => {
    // 現在のURLから job_keyword を抽出
    const currentPath = window.location.pathname;
    const jobKeyword = extractJobKeywordFromCurrentPath(currentPath);
    
    let searchString = '';
    
    if (searchParam.keyword !== '' && searchParam.location !== '') {
        // キーワード勤務地掛け合わせ: {job_keyword}-s-q-{keyword}-s-l-{location}
        searchString = 
            jobKeyword + '-s-q-' +
            searchParam.keyword.replaceAll('%20', '-').replaceAll(' ', '-') +
            '-s-l-' +
            searchParam.location.replaceAll(' ', '-') +
            composeQueryParameter(searchParam);
    } else if (searchParam.location !== '') {
        // 勤務地のみ: {job_keyword}-s-l-{location}
        searchString =
            jobKeyword + '-s-l-' +
            searchParam.location.replaceAll(' ', '-') +
            composeQueryParameter(searchParam);
    } else if (searchParam.keyword !== '') {
        // キーワードのみ: {job_keyword}-s-q-{keyword}
        searchString =
            jobKeyword + '-s-q-' +
            searchParam.keyword.replaceAll('%20', '-').replaceAll(' ', '-') +
            composeQueryParameter(searchParam);
    }
    
    return searchString;
};

/**
 * Jobs-In形式の検索文字列を生成
 * パターン: {keyword}-jobs-in-{location}
 *
 * @param {SearchParam} searchParam
 * @return {string}
 */
const composeJobsInFormatSearchString = (searchParam) => {
    let searchString = '';
    
    if (searchParam.keyword !== '' && searchParam.location !== '') {
        // キーワード勤務地掛け合わせ
        searchString +=
            searchParam.keyword
                .replaceAll('%20', '-')
            + '-jobs-in-'
            + searchParam.location.replaceAll(' ', '-')
            + composeQueryParameter(searchParam);
    } else if (searchParam.location !== '') {
        // 勤務地のみ
        searchString +=
            'jobs-in-'
            + searchParam.location.replaceAll(' ', '-')
            + composeQueryParameter(searchParam);
    } else if (searchParam.keyword !== '') {
        // キーワードのみ
        searchString +=
            searchParam.keyword
                .replaceAll('%20', '-')
            + '-jobs'
            + composeQueryParameter(searchParam);
    }
    
    return searchString;
};

/**
 * 現在のURLパスから job_keyword を抽出
 * 
 * @param {string} currentPath 
 * @return {string}
 */
const extractJobKeywordFromCurrentPath = (currentPath) => {
    // パスの先頭の "/" を除去
    const pathWithoutLeadingSlash = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;
    
    if (pathWithoutLeadingSlash.includes('-s-q-')) {
        return pathWithoutLeadingSlash.split('-s-q-')[0];
    } else if (pathWithoutLeadingSlash.includes('-s-l-')) {
        return pathWithoutLeadingSlash.split('-s-l-')[0];
    }
    
    // SQSL形式の識別子が見つからない場合のデフォルト値
    return DEFAULT_JOB_KEYWORD;
};

/**
 * 検索結果URLのクエリパラメータ部分を組み立てる
 * クエリパラメータ順序は以下
 * Job Type > 給与種別 > 給与下限 > 給与上限 > 投稿日 > 距離 > 特徴（ID順でカンマ区切り）> 送客課金 > ページング > ユニークID
 *
 * @param {SearchParam} searchParam
 * @return {string}
 */
export const composeQueryParameter = (searchParam) => {
    let paramString = '';
    if (searchParam.jobType !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.jobType}=` + searchParam.jobType;
    }
    if (searchParam.salaryType !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.salaryType}=` + searchParam.salaryType;
    }
    if (searchParam.salaryMin !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.salaryMin}=` + searchParam.salaryMin;
    }
    if (searchParam.salaryMax !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.salaryMax}=` + searchParam.salaryMax;
    }
    if (searchParam.datePosted !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.datePosted}=` + searchParam.datePosted;
    }
    if (searchParam.distance !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.distance}=` + searchParam.distance;
    }
    if (searchParam.features.length !== 0) {
        const joinMark = paramString === '' ? '?' : '&';
        const sortedFeatureParams = searchParam.features.sort((pre, next) => pre - next);
        paramString += `${joinMark}${SearchParamKeyDefinition.features}=`;
        for (const feature of sortedFeatureParams) {
            paramString += feature + '%2C';
        }
        paramString = paramString.replace(/%2C$/, '');
    }
    if (searchParam.type !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.type}=` + searchParam.type;
    }
    if (searchParam.page !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.page}=` + searchParam.page;
    }
    if (searchParam.uniqueId !== '') {
        const joinMark = paramString === '' ? '?' : '&';
        paramString += `${joinMark}${SearchParamKeyDefinition.uniqueId}=` + searchParam.uniqueId;
    }
    return paramString;
}

/**
 * 検索結果URlを生成
 *
 * @param {string} searchString
 * @return {string}
 */
export const composeSearchUrlFromSearchString = (searchString) => {
    return '/' + searchString;
}

/**
 * 詳細URLを生成
 *
 * @param {string} uniqueId
 * @return {string}
 */
export const composeDetailUrlFromUniqueId = (uniqueId) => {
    return '/jb/' + encodeURI(uniqueId);
}

/**
 * 有効求人チェックAPIのURL生成
 *
 * @param jobs
 * @return {string}
 */
export const composeCheckValidityApiUrl = (jobs) => {
    let uniqueIds = '';

    jobs.forEach((job, i) => {
        const uniqueId = job.uniqueId;

        if (i === 0) {
            uniqueIds += uniqueId;
        } else {
            uniqueIds += (',' + uniqueId);
        }
    });

    return '/api/check-job-valid?uniqueIds=' + encodeURIComponent(uniqueIds);
}
